/**
 * The assistant's mark as a pure gesture machine (UX spec v3 §C.3, hand-off §C.16) —
 * `bindAim` of `docs/design/v3/tools/shared.js`, with time passed in so every path is a
 * unit test, not a browser race.
 *
 * Tap (released within the slop, before the hold commits) = the chat, text first.
 * Hold `hold` ms = the mic ("committed"). Slide up `drag` px = the mic ("drag-commit").
 * Anything else — sideways, down, a slide released early, the system taking the
 * pointer — is a cancel: nothing happens. After a commit the finger can lift: the
 * release adds nothing (listening is hands-free).
 *
 * With the microphone off nothing charges and nothing slides; a hold says so once
 * ("voice-off") and a tap still opens the chat.
 */
export interface AimTuning {
  /** Hold to commit (ms). Android's long-press timeout; under iOS's 500 ms. */
  readonly hold: number;
  /** The charge ring shows from here (ms) — just past Android's 100 ms tap timeout. */
  readonly from: number;
  /** Movement that still counts as a tap (px). */
  readonly slop: number;
  /** Slide-up distance that commits (px). */
  readonly drag: number;
  /** Sideways travel that ends a gesture before it slides (px). */
  readonly side: number;
  /** Sideways travel that ends a slide already under way (px). */
  readonly sideDrag: number;
}

export const AIM: AimTuning = { hold: 400, from: 120, slop: 10, drag: 96, side: 48, sideDrag: 64 };

export type AimState = "idle" | "pressed" | "charging" | "committed" | "dragging" | "drag-commit" | "cancel";

export interface AimView {
  readonly state: AimState;
  /** 0 → 1: the charge ring, or how far up the slide has come. */
  readonly p: number;
  /** How far the drop follows the finger up (px). */
  readonly dy: number;
}

export type AimEffect =
  | { readonly kind: "tap" }
  | { readonly kind: "commit"; readonly how: "hold" | "drag" }
  | { readonly kind: "cancel"; readonly why: "slide" | "released" | "system" }
  | { readonly kind: "voice-off" };

type Phase = "down" | "charging" | "drag" | "off" | "committed";

interface Touch {
  readonly x0: number;
  readonly y0: number;
  readonly t0: number;
  phase: Phase;
  moved: number;
}

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/**
 * How far the drop follows a finger `up` px up the lift track: 35 %, at most 34 px. Shared with the first-run welcome's
 * lesson (DCTA-216, app/welcome-lesson.ts), so it can never show a drag the mark does not do.
 */
export const dyOf = (up: number): number => Math.round(Math.min(Math.max(0, up) * 0.35, 34));

export class AimGesture {
  readonly #t: AimTuning;
  readonly #voiceOff: () => boolean;
  #g: Touch | undefined;
  #view: AimView = { state: "idle", p: 0, dy: 0 };

  constructor(tuning: AimTuning, voiceOff: () => boolean) {
    this.#t = tuning;
    this.#voiceOff = voiceOff;
  }

  get view(): AimView {
    return this.#view;
  }

  /** A gesture is in progress (the finger is down and nothing has ended it). */
  get active(): boolean {
    return this.#g !== undefined && this.#g.phase !== "committed";
  }

  down(x: number, y: number, t: number): void {
    this.#g = { x0: x, y0: y, t0: t, phase: "down", moved: 0 };
    this.#view = { state: "pressed", p: 0, dy: 0 };
  }

  /** A frame: the hold's progress. */
  tick(t: number): AimEffect | undefined {
    const g = this.#g;
    if (g === undefined || (g.phase !== "down" && g.phase !== "charging")) return undefined;
    const held = t - g.t0;
    if (this.#voiceOff()) {
      if (held < this.#t.hold) return undefined;
      g.phase = "off";
      this.#view = { state: "idle", p: 0, dy: 0 };
      return { kind: "voice-off" };
    }
    if (held < this.#t.from) return undefined;
    const p = Math.min(1, (held - this.#t.from) / (this.#t.hold - this.#t.from));
    if (p >= 1) return this.#commit("hold");
    g.phase = "charging";
    this.#view = { state: "charging", p: round3(p), dy: 0 };
    return undefined;
  }

  move(x: number, y: number, _t: number): AimEffect | undefined {
    const g = this.#g;
    if (g === undefined || g.phase === "committed") return undefined;
    const dx = x - g.x0;
    const up = g.y0 - y;
    g.moved = Math.max(g.moved, Math.hypot(dx, up));
    if (g.phase !== "drag") {
      if (g.moved <= this.#t.slop) return undefined;
      if (up > this.#t.slop && Math.abs(dx) < this.#t.side && g.phase !== "off" && !this.#voiceOff()) g.phase = "drag";
      else return this.#cancel("slide");
    }
    if (Math.abs(dx) > this.#t.sideDrag || up < -this.#t.slop) return this.#cancel("slide");
    if (up >= this.#t.drag) return this.#commit("drag");
    const p = Math.max(0, Math.min(1, up / this.#t.drag));
    this.#view = { state: "dragging", p: round3(p), dy: dyOf(up) };
    return undefined;
  }

  up(t: number): AimEffect | undefined {
    const g = this.#g;
    if (g === undefined) return undefined;
    if (g.phase === "committed") {
      this.#g = undefined;
      return undefined;
    }
    if (g.phase === "drag") return this.#cancel("released");
    if (g.phase === "off") {
      this.#g = undefined;
      this.#view = { state: "idle", p: 0, dy: 0 };
      return undefined;
    }
    // A release with no frame since the hold's time (a starved rAF) is a hold, not a tap.
    const effect = this.tick(t);
    if (effect !== undefined) {
      this.#g = undefined;
      return effect;
    }
    if (g.moved <= this.#t.slop && t - g.t0 < this.#t.hold) {
      this.#g = undefined;
      this.#view = { state: "idle", p: 0, dy: 0 };
      return { kind: "tap" };
    }
    return this.#cancel("released");
  }

  /** The system took the pointer (`pointercancel`): nothing happens. */
  cancel(): AimEffect | undefined {
    const g = this.#g;
    if (g === undefined || g.phase === "committed") return undefined;
    return this.#cancel("system");
  }

  /** The drained state is over: back to rest. */
  settle(): void {
    if (this.#g === undefined || this.#g.phase === "committed") this.#view = { state: "idle", p: 0, dy: 0 };
    if (this.#g?.phase === "committed") this.#g = undefined;
  }

  #commit(how: "hold" | "drag"): AimEffect {
    const g = this.#g;
    if (g !== undefined) g.phase = "committed";
    this.#view = how === "drag" ? { state: "drag-commit", p: 1, dy: 34 } : { state: "committed", p: 1, dy: 0 };
    return { kind: "commit", how };
  }

  #cancel(why: "slide" | "released" | "system"): AimEffect {
    this.#g = undefined;
    this.#view = { state: "cancel", p: 0, dy: 0 };
    return { kind: "cancel", why };
  }
}
